/**
 * 앱 UI에서 지원하는 공식 BCP47 언어 코드(주 언어 부분만 사용).
 * 단일 정의: `config/locales` — i18n·푸터·GNB `LanguageSwitcher`와 동기화
 */
import { SUPPORTED_LOCALE_CODES, type SupportedLocaleCode } from '@/config/locales';

export const SUPPORTED_LOCALES = SUPPORTED_LOCALE_CODES;
export type SupportedLocale = SupportedLocaleCode;

/**
 * 푸터 등 언어 스위처: 공식 언어만 노출하고, 라벨은 자국어 표기(endonym)를 사용.
 */
export const LOCALE_ENDONYMS: Record<SupportedLocale, string> = {
	ko: '한국어',
	en: 'English',
	ar: 'العربية',
	mn: 'Монгол хэл',
	id: 'Bahasa Indonesia',
};

export function normalizeLocaleTag(raw: string | undefined | null): string {
	if (raw == null || String(raw).trim() === '') return '';
	return String(raw).toLowerCase().split('-')[0];
}

const INTL_LOCALE_MAP: Record<SupportedLocale, string> = {
	ko: 'ko-KR',
	en: 'en-US',
	ar: 'ar',
	mn: 'mn-MN',
	id: 'id-ID',
};

/** 화면 표시용 Intl locale. 공식 언어 외 입력은 영어(en-US)로 fallback. */
export function resolveIntlLocale(raw: string | undefined | null): string {
	return INTL_LOCALE_MAP[resolveSupportedLocale(raw)];
}

/** 브라우저/저장값 → 공식 지원 코드만. 비공식 언어는 영어 fallback. */
export function resolveSupportedLocale(raw: string | undefined | null): SupportedLocale {
	const base = normalizeLocaleTag(raw);
	if ((SUPPORTED_LOCALES as readonly string[]).includes(base)) {
		return base as SupportedLocale;
	}
	return 'en';
}

/**
 * `navigator.languages` 를 순서대로 훑어 **첫** 공식 지원 코드를 쓰고, 없으면 `en`.
 * (첫 태그만 쓰는 i18next 기본 동작과 달리 `ja,ko` → `ko` 선택)
 */
export function pickFromNavigatorLanguageList(): SupportedLocale {
	if (typeof window === 'undefined' || typeof navigator === 'undefined') {
		return 'en';
	}
	const list: string[] =
		typeof navigator.languages === 'object' && navigator.languages != null && navigator.languages.length > 0
			? Array.from(navigator.languages)
			: [navigator.language || 'en'];
	for (const raw of list) {
		const base = normalizeLocaleTag(raw);
		if ((SUPPORTED_LOCALES as readonly string[]).includes(base)) {
			return base as SupportedLocale;
		}
	}
	return 'en';
}

/**
 * 백엔드 API `language` 파라미터에 사용.
 * 공식 UI 언어는 그대로 요청하고, 비공식 언어는 영어 데이터로 요청한다.
 */
export function getApiLanguageCode(raw: string | undefined | null): SupportedLocale {
	return resolveSupportedLocale(raw);
}

/**
 * 백엔드·목업이 ko | en 만 구분할 때 사용.
 * UI가 한국어(`ko`)가 아니면 영어 데이터(en)를 요청·표시하는 편이 맞습니다.
 */
export function apiPreferredKoEn(lang: string | undefined | null): 'ko' | 'en' {
	return normalizeLocaleTag(lang) === 'ko' ? 'ko' : 'en';
}

/**
 * API·DB에서 내려오는 클래스명·기관명 등은 백엔드 다국어 필드(또는 locale 쿼리)와 별도 합의 후
 * `getLocalizedClassItem` 등에서 소비. 이 모듈은 UI 번들(i18n JSON) 언어 코드만 다룸.
 */
